import { Injectable, signal } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpService } from 'src/app/core/services/http/http.service';
import { ApiMethod } from 'src/app/core/services/const';

/**
 * C5 Schritt 1 — „Heute in deinem Betrieb".
 *
 * Der Unterschied zwischen einem Chatfenster und einem Helfer: Ein Chatfenster fragt „Wobei
 * kann ich helfen?". Ein Helfer *weiss*, was ansteht. Diese Klasse holt den Zustand.
 *
 * ---
 *
 * ## ⚠️ Warum NICHT `get_briefing`
 *
 * `get_briefing` bündelt genau diese vier Quellen — aber es ist ein **Chatbot-Werkzeug**,
 * kein Endpunkt. Es aufzurufen hiesse, eine KI-Anfrage auszugeben, nur um den leeren
 * Zustand zu füllen; in einer Demo mit zehn Anfragen wäre ein Zehntel verbraucht, bevor der
 * Besucher ein Wort getippt hat. Die Quellen direkt zu lesen kostet keine.
 *
 * ## Warum ein eigener, schlanker Service
 *
 * `DashboardApiService` kennt beide Endpunkte bereits — er lebt aber im **lazy geladenen**
 * Dashboard-Modul und zöge dessen Modellbaum in das eager geladene Hilfe-Modul. Zwei
 * `requestCall` sind billiger als diese Kopplung.
 *
 * ## Fehler sind hier kein Fehler
 *
 * Wer das Dashboard-Recht nicht hat, bekommt 403. Wer keine offenen Punkte hat, bekommt eine
 * leere Liste. **Beides führt zum selben Ergebnis: die Karte entfällt ersatzlos**, und der
 * heutige Empty-State greift. Ein Assistent, der „Zustand konnte nicht geladen werden"
 * meldet, hat aus einer Verbesserung eine Fehlermeldung gemacht.
 *
 * ## Und er darf nicht drängeln
 *
 * Drei Zeilen, keine roten Warnfarben, kein Zähler am Launcher. Der Assistent ist ein
 * Helfer, kein Mahnsystem — deshalb {@link MAX_ROWS} und keine Dringlichkeitsfarben.
 */

/** Eine antippbare Zeile im leeren Zustand. */
export interface PulseRow {
  /** Art des offenen Punkts — bestimmt Text und Folgefrage. */
  kind: PulseKind;
  /** Wie viele es sind (mindestens 1). */
  count: number;
}

export type PulseKind =
  | 'openShift'
  | 'absenceRequest'
  | 'shiftSwap'
  | 'overtime'
  | 'changeRequest';

/**
 * Höchstens drei Zeilen. Das ist keine Layout-Vorliebe: Eine Liste, die alles zeigt, was
 * offen ist, liest sich als Mahnung — und genau davor warnt der Entwurf ausdrücklich.
 */
const MAX_ROWS = 3;

/**
 * Die `Type`-Werte des Backends (`ActionItemDto.Type`) auf unsere Arten.
 *
 * ⚠️ **Das Backend sendet die ZAHL, nicht den Namen.** `ActionItemType` ist ein C#-Enum
 * ohne `JsonStringEnumConverter`; über die Leitung kommt `"Type": 0`. Bis zum 10.09.2026
 * standen hier nur die Namen — `TYPE_MAP['0']` ist `undefined`, jedes Element fiel durch,
 * `rows` blieb leer, und die Karte „Heute in deinem Betrieb" konnte NIE erscheinen.
 *
 * Der Fehler war doppelt unsichtbar: Der Service verschluckt Fehler bewusst (siehe
 * Klassen-Doku), und eine leere Liste ist ein gültiger Zustand („nichts steht an"). Der
 * Bericht führte den Punkt deshalb als „blockiert durch HTTP 500" — der 500er war ein
 * Auth-Zustand und lediglich der lautere von zwei Gründen. Gemessen am laufenden System:
 * `GET /Dashboard/Widget/ActionItems` antwortet mit angemeldeter Sitzung **200** mit 6
 * Elementen, alle `Type: 0`.
 *
 * Beide Schreibweisen bleiben stehen: Die Zahlen sind der Ist-Zustand, die Namen greifen,
 * sobald das Backend einen String-Konverter bekommt — und die Vitest-Fixtures nutzen sie.
 * Reihenfolge und Werte aus `Application/Dashboard/Models/Shared/ActionItemType.cs`.
 *
 * `TimeCorrection` (4) und `ComplianceWarning` (5) haben bewusst KEINE Zeile: Für sie gibt
 * es keine Folgefrage, die ein Werkzeug trifft — eine Zeile ohne Ziel wäre eine Sackgasse.
 */
const TYPE_MAP: Record<string, PulseKind> = {
  0: 'absenceRequest',
  1: 'openShift',
  2: 'shiftSwap',
  3: 'overtime',
  AbsenceRequest: 'absenceRequest',
  OpenShift: 'openShift',
  ShiftSwap: 'shiftSwap',
  OvertimeApproval: 'overtime',
  ChangeRequest: 'changeRequest',
};

/**
 * Reihenfolge der Zeilen — nach Handlungsdruck, nicht nach Anzahl.
 *
 * Eine unbesetzte Schicht ist ein Loch im Plan und steht deshalb oben, auch wenn zehn
 * Urlaubsanträge warten: Der Antrag kann bis morgen liegen, die Schicht ist morgen früh.
 */
const ORDER: PulseKind[] = [
  'openShift',
  'absenceRequest',
  'shiftSwap',
  'changeRequest',
  'overtime',
];

@Injectable({ providedIn: 'root' })
export class CompanyPulseService {
  /**
   * `null` = noch nicht geladen (Skelett zeigen), `[]` = nichts offen (Karte entfällt).
   *
   * Der Unterschied ist der Grund für `null` statt eines leeren Startwerts: Ohne ihn zeigte
   * die Oberfläche beim Öffnen für einen Sekundenbruchteil „nichts steht an" und danach drei
   * Zeilen — was aussieht, als hätte sie sich vertan.
   */
  private readonly _rows = signal<PulseRow[] | null>(null);
  readonly rows = this._rows.asReadonly();

  /** Verhindert einen zweiten Abruf, solange der erste läuft oder frisch beantwortet ist. */
  private loadedAt = 0;
  private inFlight = false;

  /**
   * Wie lange ein Ergebnis gilt.
   *
   * Der leere Zustand wird beim Öffnen des Panels gerendert, und das Panel öffnet oft
   * mehrmals hintereinander. Ohne diese Frist liefe bei jedem Antippen ein Doppelabruf.
   */
  private static readonly CACHE_MS = 60_000;

  constructor(private http: HttpService) {}

  /**
   * Lädt den Zustand — höchstens einmal je Minute, und niemals blockierend.
   *
   * @param now Zeitstempel, als Parameter für Testbarkeit (kein `Date.now()` in der Logik).
   */
  refresh(now: number = Date.now()): void {
    if (this.inFlight) return;
    if (this._rows() !== null && now - this.loadedAt < CompanyPulseService.CACHE_MS) return;
    this.inFlight = true;

    forkJoin({
      actions: this.http.requestCall('/Dashboard/Widget/ActionItems', ApiMethod.GET).pipe(
        catchError(() => of(null)),
      ),
    })
      .pipe(
        map(({ actions }) => this.toRows(actions)),
        catchError(() => of([] as PulseRow[])),
      )
      .subscribe((rows) => {
        this._rows.set(rows);
        this.loadedAt = now;
        this.inFlight = false;
      });
  }

  /** Nach „Neue Unterhaltung": der Zustand darf sich inzwischen geändert haben. */
  invalidate(): void {
    this.loadedAt = 0;
  }

  /**
   * Antwort → Zeilen: nach Art gruppieren, zählen, nach Handlungsdruck sortieren, kappen.
   *
   * Liest beide Schreibweisen (Erfolgs-Bodies sind PascalCase, Fehler-Bodies camelCase —
   * und Tests liefern gern camelCase).
   */
  private toRows(raw: any): PulseRow[] {
    const data = raw?.Data ?? raw?.data ?? raw;
    const items = (data?.Items ?? data?.items ?? []) as any[];
    if (!Array.isArray(items) || items.length === 0) return [];

    const counts = new Map<PulseKind, number>();
    for (const item of items) {
      const kind = TYPE_MAP[String(item?.Type ?? item?.type ?? '')];
      if (!kind) continue;
      counts.set(kind, (counts.get(kind) ?? 0) + 1);
    }

    return ORDER.filter((k) => (counts.get(k) ?? 0) > 0)
      .slice(0, MAX_ROWS)
      .map((kind) => ({ kind, count: counts.get(kind)! }));
  }
}
